/**
 * Sales Report Page - with sale details modal and graphical charts
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { reportsAPI } from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { DataGrid } from '../../components/ui/DataGrid';
import { PageHeader } from '../../components/ui/PageHeader';
import { SaleDetailModal } from '../../components/ui/SaleDetailModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { SalesCharts } from '../../components/reports/SalesCharts';
import { toast } from 'sonner';
import { BarChart3, DollarSign, TrendingUp, Receipt, Eye, PieChart, Table } from 'lucide-react';

export default function SalesReport() {
  const { t } = useTranslation();
  const { formatCurrency } = useInstance();
  const gridRef = useRef(null);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const [filters, setFilters] = useState({
    start_date: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return d.toISOString().split('T')[0];
    })(),
    end_date: new Date().toISOString().split('T')[0],
  });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const res = await reportsAPI.getSales({
        start_date: filters.start_date + 'T00:00:00',
        end_date: filters.end_date + 'T23:59:59',
      });
      console.log('Sales report:', res.data);
      setReport(res.data);
    } catch (err) {
      console.error('Error fetching sales:', err);
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchReport();
  }, []);

  const handleExportCSV = useCallback(() => {
    gridRef.current?.exportCSV(false);
  }, []);

  const handleExportJSON = useCallback(() => {
    const data = gridRef.current?.exportJSON(false);
    if (!data?.length) return;
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vendas_${filters.start_date}_${filters.end_date}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filters]);

  const handleExportPDF = useCallback(async () => {
    try {
      toast.loading('Gerando PDF...');
      const res = await reportsAPI.getSalesPDF({
        start_date: filters.start_date,
        end_date: filters.end_date,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendas_${filters.start_date}_${filters.end_date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('PDF gerado com sucesso!');
    } catch (err) {
      toast.dismiss();
      console.error('Error generating PDF:', err);
      toast.error('Erro ao gerar PDF');
    }
  }, [filters]);

  const handleViewSale = (sale) => {
    setSelectedSale(sale);
    setDetailModalOpen(true);
  };

  const filterDefinitions = useMemo(() => [
    {
      key: 'start_date',
      label: 'Data Inicial',
      type: 'date',
    },
    {
      key: 'end_date',
      label: 'Data Final',
      type: 'date',
    },
  ], []);

  const columnDefs = useMemo(() => [
    {
      field: 'sale_number',
      headerName: '#',
      flex: 0.5,
      valueFormatter: (params) => `#${params.value || params.data.id?.slice(0, 6)}`,
    },
    {
      field: 'created_at',
      headerName: 'Data/Hora',
      flex: 1,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      field: 'items',
      headerName: 'Itens',
      flex: 0.5,
      valueFormatter: (params) => params.value?.length || 0,
    },
    {
      field: 'payments',
      headerName: 'Pagamento',
      flex: 1,
      valueFormatter: (params) => {
        const payments = params.value || [];
        return payments.map(p => p.payment_method_name).join(', ') || '-';
      },
    },
    {
      field: 'total',
      headerName: 'Total',
      flex: 0.8,
      cellRenderer: (params) => (
        <span className="font-mono font-semibold text-primary">
          {formatCurrency(params.value || 0)}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      flex: 0.5,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => handleViewSale(params.data)}
          title="Ver detalhes"
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ], [formatCurrency]);

  return (
    <div className="sales-report-page" data-testid="sales-report-page">
      <PageHeader
        title="Relatório de Vendas"
        icon={BarChart3}
        subtitle="Acompanhe todas as vendas realizadas"
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={setFilters}
        onSearch={fetchReport}
        onClearFilters={() => {
          const d = new Date();
          d.setDate(d.getDate() - 30);
          setFilters({
            start_date: d.toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0],
          });
        }}
        onExportCSV={handleExportCSV}
        onExportJSON={handleExportJSON}
        onExportPDF={handleExportPDF}
      />

      {report && (
        <>
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total de Vendas</CardTitle>
                <DollarSign className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">{formatCurrency(report.total_amount || 0)}</div>
                <p className="text-xs text-muted-foreground">
                  {report.total_sales || 0} vendas no período
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ticket Médio</CardTitle>
                <TrendingUp className="h-4 w-4 text-emerald-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">
                  {formatCurrency(report.total_sales ? report.total_amount / report.total_sales : 0)}
                </div>
                <p className="text-xs text-muted-foreground">valor médio por venda</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Formas de Pagamento</CardTitle>
                <Receipt className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">{report.payments_by_method?.length || 0}</div>
                <p className="text-xs text-muted-foreground">métodos utilizados</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs for Table and Charts */}
          <Tabs defaultValue="charts" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="charts" className="flex items-center gap-2">
                <PieChart className="h-4 w-4" />
                Gráficos
              </TabsTrigger>
              <TabsTrigger value="table" className="flex items-center gap-2">
                <Table className="h-4 w-4" />
                Detalhamento
              </TabsTrigger>
            </TabsList>

            <TabsContent value="charts">
              <SalesCharts report={report} formatCurrency={formatCurrency} />
            </TabsContent>

            <TabsContent value="table">
              {/* Sales Grid */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Vendas do Período</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <DataGrid
                    ref={gridRef}
                    title="Detalhamento das Vendas"
                    columnDefs={columnDefs}
                    rowData={report.sales || []}
                    loading={loading}
                    getRowId={(params) => params.data.id}
                    onRowDoubleClick={handleViewSale}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {!report && !loading && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Clique em Buscar para carregar o relatório
          </CardContent>
        </Card>
      )}

      <SaleDetailModal
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        sale={selectedSale}
      />
    </div>
  );
}
